import { useAppDispatch } from '../../../../../redux/hooks'
import { FC, memo, MouseEvent, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { CancellationPolicyTransport } from '../../../../../transports/search-result-listing-static-transport'
import type { StyledSvgComponent } from '../../../../../types/svg'
import {
    Container,
    InnerContainer,
    Input,
    Label,
    LabelHandle,
    LabelIcon,
    OuterContainer,
    ReadMoreText,
    Text,
    TextContainer,
} from './cancellation-policy-filter-styles'
import { encodeToBase64 } from '../../../../../utils/encoding-utils'
import { filterGATracking } from '../../utils'
import { FilterModalType, FilterType } from '../../types'
import { FilterItemTransport } from '../../../../../transports/filters-configuration-transport'
import { filterClick, getFilterError, getRegionNodeId, pushModal } from '../../../../../redux/filters'

import { useRequestUserConfigContext } from '../../../../../hooks/request'

import CrossIcon from '../../../../../assets/icons/filter-icons/cross.svg'
import TickIcon from '../../../../../assets/icons/filter-icons/tick.svg'

interface Props {
    filterItems: FilterItemTransport[]
    specificFilterStaticData: CancellationPolicyTransport
    shouldCommit?: boolean
}

const CancellationPolicyFilter: FC<Props> = ({ filterItems, specificFilterStaticData, shouldCommit = false }) => {
    const dispatch = useAppDispatch()
    const error = useSelector(getFilterError)
    const regionNodeId = useSelector(getRegionNodeId)
    const user = useRequestUserConfigContext()
    const [isChecked, setIsChecked] = useState<boolean>(filterItems[0].checked)

    const handleItemClick = useCallback(
        (event: MouseEvent<HTMLLabelElement>) => {
            event.preventDefault()
            filterGATracking({
                type: FilterType.CANCELLATION_POLICY,
                name: filterItems[0].title,
                value: filterItems[0].value,
                isChecked: !isChecked,
                regionNodeId,
            })
            setIsChecked(!isChecked)
            const filterItemUrl = new URL(filterItems[0].absoluteLink)
            dispatch(filterClick(encodeToBase64(filterItemUrl.pathname + filterItemUrl.search), shouldCommit, user))
        },
        [filterItems, isChecked, dispatch, regionNodeId, user, shouldCommit],
    )

    const handleReadMoreModal = useCallback(
        (e: MouseEvent<HTMLSpanElement>) => {
            e.preventDefault()
            e.stopPropagation()
            dispatch(pushModal(FilterModalType.CANCELLATION_POLICY))
        },
        [dispatch],
    )

    useEffect(() => {
        setIsChecked(filterItems[0].checked)
    }, [filterItems, error])

    return (
        <OuterContainer>
            <InnerContainer>
                <TextContainer>
                    <Text>
                        {specificFilterStaticData.description}
                        <ReadMoreText onClick={handleReadMoreModal}>{specificFilterStaticData.readMore}</ReadMoreText>
                    </Text>
                </TextContainer>

                <Container>
                    <Label onClick={handleItemClick} $selected={isChecked}>
                        <Input type="checkbox" defaultChecked={isChecked} />
                        <LabelHandle>
                            {isChecked ? (
                                <LabelIcon as={TickIcon as StyledSvgComponent} aria-label="label icon tick" />
                            ) : (
                                <LabelIcon as={CrossIcon as StyledSvgComponent} aria-label="label icon cross" />
                            )}
                        </LabelHandle>
                    </Label>
                </Container>
            </InnerContainer>
        </OuterContainer>
    )
}

export default memo(CancellationPolicyFilter)
